import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import PostMaturityInterestPanel from '../Common/PostMaturityInterestPanel';

const LatePaymentManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nicFilter, setNicFilter] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [totalArrears, setTotalArrears] = useState(0);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchCustomerArrears();
  }, [startDate, endDate]);

  useEffect(() => {
    // Debounce NIC filter
    const timer = setTimeout(() => {
      fetchCustomerArrears();
    }, 300);
    return () => clearTimeout(timer);
  }, [nicFilter]);

  const fetchCustomerArrears = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (nicFilter && nicFilter.trim()) {
        params.append('nic', nicFilter.trim());
      }
      
      const response = await api.get(`/admin/late-payments/customer-arrears?${params.toString()}`);
      const data = response.data || {};
      setCustomers(data.customers || []);
      setTotalArrears(data.totalArrears || 0);
    } catch (err) {
      console.error('Error fetching customer arrears:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setCustomers([]);
      setTotalArrears(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerClick = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetails(null);
    setLoadingDetails(true);
    setDetailsDialogOpen(true);
    setTabValue(0);
    
    try {
      const response = await api.get(`/admin/late-payments/customer/${customer.customerId}/details`);
      setCustomerDetails(response.data);
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load customer details');
    } finally {
      setLoadingDetails(false);
    }
  };


  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return `Rs ${num.toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Box>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontWeight: 700,
          color: '#0D47A1',
          mb: 3,
          fontSize: { xs: '1.75rem', sm: '2rem' },
        }}
      >
        Late Payment Management
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Filter by NIC"
              value={nicFilter}
              onChange={(e) => setNicFilter(e.target.value)}
              placeholder="Enter NIC to search"
              helperText={loading ? 'Loading...' : `Found ${customers.length} customer(s)`}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Card */}
      <Card 
        sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(211, 47, 47, 0.3)',
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalanceIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Arrears
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(totalArrears)}
              </Typography>
              <Typography variant="body2">
                Across {customers.length} customer(s)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : customers.length === 0 ? (
        <Alert severity="info">
          No customers with arrears found for the selected date range.
        </Alert>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#0D47A1',
              borderRadius: 4,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Customer Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>NIC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Loan ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Overdue Installments</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Post-Maturity Interest</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Total Arrears</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <TableRow 
                  key={customer.customerId}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => handleCustomerClick(customer)}
                >
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{customer.customerName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', sm: 'table-cell' } }}>{customer.customerNic}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip label={`Loan #${customer.loanId}`} size="small" color="primary" variant="outlined" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip 
                      label={customer.overdueInstallmentCount} 
                      size="small" 
                      color="error" 
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    {Number(customer.postMaturityInterest || 0) > 0 ? (
                      <Chip label={formatCurrency(customer.postMaturityInterest)} color="warning" size="small" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {formatCurrency(customer.totalArrears)}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleCustomerClick(customer)}
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        padding: { xs: '4px 8px', sm: '6px 16px' },
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Customer Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Customer Arrears Details - {selectedCustomer?.customerName}
            </Typography>
            <IconButton onClick={() => setDetailsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : customerDetails ? (
            <>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                <Tab label="Customer Information" />
                <Tab label="Overdue Installments" />
                <Tab label="Late Interest Details" />
              </Tabs>

              {/* Customer Information Tab */}
              {tabValue === 0 && customerDetails.customer && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      Total Arrears: {formatCurrency(customerDetails.totalArrears)}
                    </Typography>
                    <Typography variant="body2">
                      {customerDetails.overdueCount} overdue installment(s)
                      {Number(customerDetails.postMaturityInterest || 0) > 0 && (
                        <> · Post-maturity interest: {formatCurrency(customerDetails.postMaturityInterest)}</>
                      )}
                    </Typography>
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Personal Information</Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography><strong>Name:</strong> {customerDetails.customer.fullName || customerDetails.customer.nic}</Typography>
                          <Typography><strong>NIC:</strong> {customerDetails.customer.nic || 'N/A'}</Typography>
                          <Typography><strong>Phone:</strong> {customerDetails.customer.phoneNumber || 'N/A'}</Typography>
                          <Typography><strong>Address:</strong> {customerDetails.customer.address || 'N/A'}</Typography>
                          <Typography><strong>District:</strong> {customerDetails.customer.district || 'N/A'}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Spouse Information</Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography><strong>Spouse Name:</strong> {customerDetails.customer.spouseFullName || 'N/A'}</Typography>
                          <Typography><strong>Spouse NIC:</strong> {customerDetails.customer.spouseNic || 'N/A'}</Typography>
                          <Typography><strong>Spouse Phone:</strong> {customerDetails.customer.spousePhoneNumber || 'N/A'}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Overdue Installments Tab */}
              {tabValue === 1 && (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      Total Arrears: {formatCurrency(customerDetails.totalArrears)}
                    </Typography>
                    <Typography variant="body2">
                      {customerDetails.overdueCount} overdue installment(s) requiring attention
                    </Typography>
                  </Alert>

                  {customerDetails.installments && customerDetails.installments.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Installment #</strong></TableCell>
                            <TableCell><strong>Loan ID</strong></TableCell>
                            <TableCell><strong>Due Date</strong></TableCell>
                            <TableCell><strong>Days Overdue</strong></TableCell>
                            <TableCell><strong>Total Amount</strong></TableCell>
                            <TableCell><strong>Paid Amount</strong></TableCell>
                            <TableCell><strong>Amount Due</strong></TableCell>
                            <TableCell><strong>Late Interest</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerDetails.installments.map((inst) => (
                            <TableRow key={inst.installmentId}>
                              <TableCell>{inst.installmentNumber}</TableCell>
                              <TableCell>{inst.loanId}</TableCell>
                              <TableCell>{formatDate(inst.dueDate)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={`${inst.daysOverdue} days`} 
                                  color="error" 
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatCurrency(inst.totalAmount)}</TableCell>
                              <TableCell>{formatCurrency(inst.paidAmount)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                  {formatCurrency(inst.amountDue)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  See Late Interest tab (loan-level)
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={inst.status} 
                                  size="small"
                                  color={
                                    inst.status === 'OVERDUE' || inst.status === 'LATE' ? 'error' :
                                    inst.status === 'PARTIAL' ? 'warning' : 'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No overdue installments found for this customer.</Alert>
                  )}
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <PostMaturityInterestPanel breakdown={customerDetails.postMaturityInterestBreakdown} />
                </Box>
              )}
            </>
          ) : (
            <Alert severity="error">Failed to load customer details.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default LatePaymentManagement;
